/*
 *  -----------------------------------------------------------------------
 *    <copyright file="step2-poisson-trend.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import FormControl from '@mui/material/FormControl';
import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import { useLocalState, useTranslation } from 'hooks';
import { RowDataSourceTable } from 'types/graphql-global-types';
import { checkRequiredFields, validateFields } from 'utils/validation';
import DataSourceSelector from 'views/components/central-monitoring/data-source-selector';
import { FieldSelector, Formats } from 'views/components/central-monitoring/field-selector';
import PanelConstants from 'views/components/central-monitoring/panels/add-panel/panel-constants';
import SelectWithCategories from 'views/components/core/select-with-categories';
import 'views/components/central-monitoring/panels/add-panel/styles.scss';

const tables = [
    RowDataSourceTable.SUBJECT,
    RowDataSourceTable.SUBJECT_ADVERSE_EVENT,
    RowDataSourceTable.SUBJECT_ASSESSMENT,
    RowDataSourceTable.SUBJECT_CRF,
    RowDataSourceTable.SUBJECT_INFORMED_CONSENT,
    RowDataSourceTable.SUBJECT_ISSUE,
    RowDataSourceTable.SUBJECT_LAB,
    RowDataSourceTable.SUBJECT_PROTOCOL_DEVIATION,
    RowDataSourceTable.SUBJECT_QUERY,
    RowDataSourceTable.SUBJECT_VISIT,
];

const Step2PoissonTrend = forwardRef(
    ({ show, panelState, setPage, page, updateState, updateErrorState, canSave }: any, ref) => {
        const { t } = useTranslation();
        const { state: localState } = useLocalState();

        const initialState = {
            dataSource: panelState.dataSource.dataSource,
            rateBy: panelState.modelParams.rateBy,
            plotBy: panelState.modelParams.plotBy,
            dateField: panelState.dataSource.dateField,
            xAxisDataField: panelState.dataSource.xAxisDataField,
            xAxisEndDateFields:
                panelState.dataSource.xAxisEndDateFields
                    ?.map((endField: string) => localState?.fields?.find((field: any) => field.key === endField))
                    ?.filter((x: any) => x) ?? [],
            useEarliestEndDate: panelState.dataSource.useEarliestEndDate ? 'EARLIEST' : 'LATEST',
        };
        const initialErrors = {
            dataSource: '',
            rateBy: '',
            plotBy: '',
            dateField: '',
            xAxisDataField: '',
            xAxisEndDateFields: '',
            useEarliestEndDate: '',
        };
        const [state, setState] = useState({ ...initialState });
        const [errors, setErrors] = useState({ ...initialErrors } as any);

        const { plotBy, rateBy, endDateUsage } = PanelConstants;

        const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
            const error = validateFields(value, field, length, required);
            setErrors({
                ...errors,
                ...error,
            });
        };

        const onChange = (fieldName: string, length?: number, required?: boolean, key?: string, value?: any) => {
            if (fieldName === 'dataSource') {
                setState({
                    ...state,
                    dataSource: value,
                    dateField: '',
                });
                updateState({ target: { name: 'dataSource.dataSource', value } });
                updateState({ target: { name: 'dataSource.dateField', value: '' } });
            } else if (fieldName === 'xAxisEndDateFields') {
                const array = value.filter((v: any) => v).map((item: any) => item.key);
                setState({
                    ...state,
                    [fieldName]: (value || []).filter((v: any) => v !== null),
                });
                updateState({ target: { name: key || fieldName, value: array } });
            } else if (fieldName === 'useEarliestEndDate') {
                setState({
                    ...state,
                    [fieldName]: value,
                });
                updateState({ target: { name: key || fieldName, value: value === 'EARLIEST' } });
            } else {
                setState({
                    ...state,
                    [fieldName]: value,
                });
                updateState({ target: { name: key || fieldName, value } });
            }

            validate(value, fieldName, length, required);
        };

        const validationOnUpdate = () => {
            const requiredFields = [
                'dataSource',
                'rateBy',
                'plotBy',
                'dateField',
                'xAxisEndDateFields',
                'useEarliestEndDate',
                'xAxisDataField',
            ];

            return checkRequiredFields({ fields: requiredFields, state, setErrors, errors });
        };

        const onNext = () => {
            if (validationOnUpdate()) {
                updateState({ target: { name: 'dataSource.dataSource', value: state.dataSource } });
                updateState({
                    target: { name: 'dataSource.useEarliestEndDate', value: state.useEarliestEndDate === 'EARLIEST' },
                });
                updateState({
                    target: {
                        name: 'dataSource.xAxisEndDateFields',
                        value: state.xAxisEndDateFields.map((item: any) => item.key),
                    },
                });
                updateState({ target: { name: 'modelParams.rateBy', value: state.rateBy } });
                updateState({ target: { name: 'modelParams.plotBy', value: state.plotBy } });
                setPage({ ...page, number: 3, available: 3 });
            } else if (page.number !== 2) setPage({ ...page, number: 2 });
        };

        const cleanStep = () => {
            setState({ ...initialState });
            setErrors({ ...initialErrors });
        };

        useEffect(() => {
            updateErrorState(errors, 2);
        }, [errors]);

        useImperativeHandle(ref, () => ({
            nextButton() {
                onNext();
            },
            cleanStep() {
                cleanStep();
            },
            validationOnUpdate() {
                return validationOnUpdate();
            },
        }));

        return (
            <div style={!show ? { display: 'none' } : {}}>
                <div className="type-edc-wrapper mt mb-30">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <DataSourceSelector
                            label={t('admin:rowData')}
                            onChange={(value: string) => {
                                onChange('dataSource', null, false, 'dataSource.dataSource', value);
                            }}
                            fieldSelect="dataSource"
                            value={state.dataSource}
                            labelField="dataSource"
                            showDefault={false}
                            error={errors.dataSource}
                            customClassName="edit-atts-category"
                            tables={tables}
                            searchable
                            tag="PANEL"
                            disabled={!canSave}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <FieldSelector
                            customClassName="add-panel-dateField"
                            fieldSelect="dateField"
                            onChange={(value: string) => {
                                onChange('dateField', null, false, 'dataSource.dateField', value);
                            }}
                            label={t('admin:rowDataDate')}
                            labelField="dateField"
                            customId="row-data-date"
                            value={state.dateField}
                            error={errors.dateField}
                            dataSource={state.dataSource}
                            formats={[Formats.Date, Formats.PartialDate]}
                            searchable
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <SelectWithCategories
                            customClassName="edit-control-review-frequency-units"
                            showWarning={false}
                            fieldSelect="rateBy"
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(fieldName, null, false, 'modelParams.rateBy', event.target?.value);
                            }}
                            label={t('admin:rateBy')}
                            filterable={false}
                            labelField="rateBy"
                            error={errors.rateBy}
                            dataArray={rateBy}
                            customId="rate-by"
                            currentState={state.rateBy}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <FieldSelector
                            customClassName="edit-control-review-frequency-units"
                            fieldSelect="xAxisDataField"
                            onChange={(value: string) => {
                                onChange('xAxisDataField', null, false, 'dataSource.xAxisDataField', value);
                            }}
                            label={t('admin:xAxisCalculationStartDate')}
                            labelField="xAxisDataField"
                            customId="x-axis-time-data"
                            value={state.xAxisDataField}
                            error={errors.xAxisDataField}
                            dataSource={RowDataSourceTable.SUBJECT}
                            formats={[Formats.Date, Formats.PartialDate]}
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper" style={{ marginBottom: 35 }}>
                    <FormControl variant="outlined" className="form-control-type">
                        <FieldSelector
                            customClassName="form-action-type"
                            fieldSelect="xAxisEndDateFields"
                            onChange={(value: any) => {
                                if (value.includes(undefined)) return;
                                onChange('xAxisEndDateFields', null, false, 'dataSource.xAxisEndDateFields', value);
                            }}
                            label={t('admin:xAxisCalculationEndDates')}
                            labelField="xAxisEndDateFields"
                            customId="x-axis-end-dates"
                            value={state.xAxisEndDateFields.map((field: any) => ({ ...field, id: field.key }))}
                            error={errors.xAxisEndDateFields}
                            dataSource={RowDataSourceTable.SUBJECT}
                            formats={[Formats.Date, Formats.PartialDate]}
                            multiselect
                            excludeValue={(field: any) =>
                                state.xAxisEndDateFields.findIndex((item: any) => item?.key === field?.key) < 0
                            }
                            disabled={!canSave}
                        />
                    </FormControl>
                </div>
                <div className="type-edc-wrapper mt">
                    <FormControl variant="outlined" className="form-control-type mr">
                        <SelectWithCategories
                            customClassName="edit-control-review-frequency-units"
                            showWarning={false}
                            fieldSelect="useEarliestEndDate"
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(fieldName, null, false, 'dataSource.useEarliestEndDate', event.target?.value);
                            }}
                            label={t('admin:xAxisEndDateUsage')}
                            filterable={false}
                            labelField="useEarliestEndDate"
                            error={errors.useEarliestEndDate}
                            dataArray={endDateUsage}
                            customId="useEarliestEndDate"
                            currentState={state.useEarliestEndDate}
                        />
                    </FormControl>
                    <FormControl variant="outlined" className="form-control-type">
                        <SelectWithCategories
                            customClassName="edit-control-review-frequency-units"
                            showWarning={false}
                            fieldSelect="plotBy"
                            onChange={(fieldName?: string) => (event: any) => {
                                onChange(fieldName, null, false, 'modelParams.plotBy', event.target?.value);
                            }}
                            label={t('admin:plotBy')}
                            filterable={false}
                            labelField="plotBy"
                            error={errors.plotBy}
                            dataArray={plotBy}
                            customId="plot-by"
                            currentState={state.plotBy}
                        />
                    </FormControl>
                </div>
            </div>
        );
    },
);

export default Step2PoissonTrend;
